import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
export interface Folder {
  id: string;
  user_id: string;
  name: string;
  icon?: string;
  color?: string;
  display_order?: number;
  parent_id?: string;
  account_ids: string[];
  created_at: string;
  updated_at: string;
}

interface FoldersStore {
  folders: Folder[];
  isLoading: boolean;
  error: string | null;
  fetchFolders: (userId: string) => Promise<Folder[]>;
  addFolder: (folder: Omit<Folder, 'id' | 'created_at' | 'updated_at'>) => Promise<Folder>;
  updateFolder: (folderId: string, updates: Partial<Folder>) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
}

export const useFoldersStore = create<FoldersStore>((set) => ({
  folders: [],
  isLoading: false,
  error: null,

  fetchFolders: async (userId: string) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Transform the data to match the expected interface
      const transformedData = (data || []).map(folder => ({
        ...folder,
        account_ids: Array.isArray(folder.account_ids) ? folder.account_ids : [] // Ensure account_ids is always an array
      }));

      set({ folders: transformedData, isLoading: false });
      return transformedData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch folders';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      return [];
    }
  },

  addFolder: async (folder) => {
    try {
      set({ isLoading: true, error: null });

      const newFolder = {
        ...folder,
        account_ids: Array.isArray(folder.account_ids) ? folder.account_ids : [], // Ensure account_ids is always an array
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('folders')
        .insert([newFolder])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        folders: [...state.folders, data],
        isLoading: false
      }));

      toast.success('Folder created successfully');
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create folder';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  updateFolder: async (folderId, updates) => {
    try {
      set({ isLoading: true, error: null });

      // Ensure account_ids is an array if it's being updated
      const processedUpdates = {
        ...updates,
        account_ids: updates.account_ids || [], // Always provide an array
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('folders')
        .update(processedUpdates)
        .eq('id', folderId);

      if (error) throw error;

      set(state => ({
        folders: state.folders.map(folder => 
          folder.id === folderId ? { ...folder, ...processedUpdates } : folder
        ),
        isLoading: false
      }));

      toast.success('Folder updated successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update folder';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  deleteFolder: async (folderId) => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase
        .from('folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;

      set(state => ({
        folders: state.folders.filter(folder => folder.id !== folderId),
        isLoading: false
      }));

      toast.success('Folder deleted successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete folder';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  }
})); 